import { CreateExplorerEvent } from '../../../common/telemetryEvents/TelemetryEventCreator';
import { ExplorerOpenDetailEvent, TelemetryEvents } from '../../../common/telemetryEvents/TelemetryEvents';
import { AngularContext } from '../../../ts/factories/AngularContextFactory';
import { ResultHelper } from '../../../ts/helpers/ResultHelper';
import { ExplorableItemDefinitions } from '../../../ts/models/ContentDefinitions';
import { IExplorableItemTypeInfo } from '../../../ts/models/resourceInfoParts';
import { IResult } from '../../../ts/ServerApi/types/IResponse';
import { IModalInstance, IModalOptions, ModalService } from '../../../ts/services/ModalService';
import { Telemetry } from '../../../commonInfra/services/Telemetry';

export interface IDetailsDialogInAngularPlaceHelper {
  openDialog(contentInfo: IExplorableItemTypeInfo): void;
  actionFromSeparateDialogResultHandler: () => void;
  inlineEditResultHandler: (result: IResult) => void;
  dismissDialog(): void;
}

export interface OpenDetailDialogParams {
  content: IExplorableItemTypeInfo;

  // The AngularContext is necessary to pass the scope into the details dialog.
  // The scope must have been initialized from the content type's place page controller with all the
  // information needed for the details pane.
  angularContext: AngularContext;

  additionalModalOptions?: Partial<IModalOptions>;
}


export class DetailsDialogInAngularPlaceHelper implements IDetailsDialogInAngularPlaceHelper {

  private dialogInstance: IModalInstance | null = null;

  constructor(
    private refreshContentFn: () => void,
    private $q: ng.IQService,
    private angularContext: AngularContext,
    private modalService: ModalService,
  ) { }

  public openDialog(contentInfo: IExplorableItemTypeInfo, additionalModalOptions?: Partial<IModalOptions>) {

    // Don't let the dialog open more than once (TFS 840283)
    if (this.dialogInstance) {
      return;
    }

    this.dialogInstance = this.openDialogForContent({
      content: contentInfo,
      angularContext: this.angularContext,
      additionalModalOptions
    });

    // Remove our internal reference to the dialog when it gets dismissed
    this.dialogInstance.result.catch(() => {
      this.dialogInstance = null;
    });
  }

  private openDialogForContent(params: OpenDetailDialogParams): IModalInstance {
    const contentType = params.content.contentType;
    const contentDefinition = ExplorableItemDefinitions[ contentType ];
    const getDialogOptions = contentDefinition.getDetailsDialogOptions;

    if (!getDialogOptions) {
      throw new Error(`getDetailsDialogOptions not defined for ${contentType}`);
    }

    Telemetry.sendEvent(CreateExplorerEvent<ExplorerOpenDetailEvent>({
      type: TelemetryEvents.ExplorerOpenDetail,
      detailContentId: params.content.id,
      detailContentType: contentType
    }));

    const modalOptions: IModalOptions = {
      ...getDialogOptions(params.angularContext),
      ...(params.additionalModalOptions && { ...params.additionalModalOptions })
    };
    return this.modalService.open(modalOptions);
  }

  public dismissDialog(): void {
    if (this.dialogInstance) {
      this.dialogInstance.dismiss();
    }
  }

  public actionFromSeparateDialogResultHandler = () => {
    // Refreshing takes time, so refresh first and then dismiss the dialog to look less jarring
    this.refreshContentFn();
    this.dismissDialog();
  }

  public inlineEditResultHandler = (result: IResult) => {
    ResultHelper.rejectResultOnError(this.$q)(result)
    .then(() => {
      this.refreshContentFn();
      this.dismissDialog();
    });
  }
}
